import * as React from 'react';
import { observer } from 'mobx-react';
import { makeObservable, observable } from 'mobx';
import { Button } from '@shared/ui-react/Button';
import { cs } from '@shared/utils/utils-classname';
import { Ajax2 } from '@shared/Ajax';
import { Notify } from '@shared/ui-native/Notify';
import { Dropdown } from 'primereact/dropdown';

@observer
export class WorkflowApproveDialog extends React.Component<{
    task: IWorkflowTask;
    onSuccess: () => void;
    onClose: () => void;
}> {
    @observable bulkStatusEnabled = false;
    @observable bulkStatus: {
        action: string;
        value: string;
        label: string;
    } = null;
    @observable isLoading = false;
    @observable isSaving = false;
    @observable.ref statuses: {
        action: string;
        value: string;
        label: string;
    }[] = [];
    @observable documents: IWorkflowDocument[] = [];

    public readonly defaultStatuses = [
        { action: 'approve', label: __('workflows.tasks.status.approve'), value: 'approve' },
        { action: 'reject', label: __('workflows.tasks.status.reject'), value: 'reject' },
    ];

    workflow: IWorkflow = null;

    constructor(props: any) {
        super(props);
        makeObservable(this);

        if (this.isBasicTask) {
            this.bulkStatusEnabled = true;
        }
    }

    get isBasicTask() {
        return !this.props.task?.workflowUuid;
    }

    componentDidMount() {
        this.fetchStatuses();
        this.documents = JSON.parse(JSON.stringify(this.props.task.documents ?? []));
    }

    fetchStatuses = async () => {
        this.isLoading = true;
        try {
            if (!this.isBasicTask) {
                const response = await Ajax2({
                    url: kuura.getContextPath() + '/api/workflow/' + this.props.task?.workflowUuid,
                    method: 'GET',
                    params: {
                        projectUuid: kuura.getProjectUuid(),
                    },
                });
                this.statuses = [
                    ...(response.approveOption ?? []).map((option: any) => ({
                        action: 'approve',
                        value: option,
                        label: option,
                    })),
                    ...(response.rejectOption ?? []).map((option: any) => ({
                        action: 'reject',
                        value: option,
                        label: option,
                    })),
                ];

                if (!response.approveOption?.length) {
                    this.statuses.unshift(this.defaultStatuses[0]);
                }
                if (!response.rejectOption?.length) {
                    this.statuses.unshift(this.defaultStatuses[1]);
                }
                this.workflow = response;
            } else {
                this.statuses = this.defaultStatuses;
            }
        } catch (e) {
            console.log('fetchAll error:', e);
        } finally {
            this.isLoading = false;
        }
    };

    save = async () => {
        this.isSaving = true;
        try {
            const url = `${kuura.getContextPath()}/api/1/tasks/review`;

            const data = {
                ...this.props.task,
                projectUuid: kuura.getProjectUuid(),
                documents: this.documents.map(doc => ({
                    ...doc,
                    statusAction: doc.status?.action,
                    statusValue: doc.status?.value,
                })),
                statusAction: this.bulkStatus?.action,
                statusValue: this.bulkStatus?.value,
            };

            if (this.bulkStatusEnabled) {
                data.documents.forEach(doc => {
                    doc.statusAction = this.bulkStatus.action;
                    doc.statusValue = this.bulkStatus.value;
                });
            }

            const response = await Ajax2({
                url: url,
                method: 'PUT',
                contentType: 'json',
                data,
            });
            this.props.onSuccess();
        } catch (e) {
            Notify.error(e.response?.data?.title ?? e.response?.data?.message);
            console.log('save task error:', e);
        } finally {
            this.isSaving = false;
        }
    };

    get canSave() {
        if (this.isSaving) {
            return false;
        }
        if (this.bulkStatusEnabled) {
            return !!this.bulkStatus;
        }
        return this.documents?.every(doc => !!doc.status);
    }

    render() {
        const step = this.workflow ? this.workflow.stepList.find(step => step.uuid === this.props.task.workflowStepUuid) : null;

        return (
            <>
                <div className={cs('card padded form-container flex-column gap of-a', { 'overlay-loading': this.isSaving || this.isLoading })}>
                    <div className='flex-column'>
                        <strong className='fa14'>{this.props.task.name}</strong>
                        {step && (
                            <strong>
                                {__('workflows.step_n_of_total', step.orderNumber, this.workflow.stepList.length)}: {step.name}
                            </strong>
                        )}
                        {this.props.task.description && <div>{this.props.task.description}</div>}
                    </div>

                    {!this.isBasicTask && (
                        <label className='checkbox-label'>
                            <input type='checkbox' checked={this.bulkStatusEnabled} onClick={() => (this.bulkStatusEnabled = !this.bulkStatusEnabled)} readOnly />
                            <span>
                                {__('workflows.bulk_change_status')}. <span style={{ opacity: 0.7 }}>{__('workflows.bulk_change_status_description')}</span>
                            </span>
                        </label>
                    )}

                    {this.bulkStatusEnabled && (
                        <div className='form-group' style={{ maxWidth: 500 }}>
                            <label>{__('general.status')} *</label>
                            <Dropdown
                                value={this.bulkStatus}
                                options={this.statuses}
                                optionLabel='label'
                                useOptionAsValue={true}
                                onChange={e => (this.bulkStatus = e.value)}
                                disabled={this.isSaving || this.isLoading}
                                style={{ minWidth: 200 }}
                                scrollHeight='600px'
                            />
                        </div>
                    )}

                    <div className='form-group'>
                        {this.documents?.length === 0 ? (
                            <div className='padded text-center'>{__('workflows.tasks.no_items_in_task')}</div>
                        ) : (
                            <table className='simple-table'>
                                <thead>
                                    <tr>
                                        <th>
                                            {__('workflows.tasks.items_in_task')} ({this.documents.length})
                                        </th>
                                        {!this.bulkStatusEnabled && <th>{__('general.status')} *</th>}
                                    </tr>
                                </thead>
                                <tbody className='filefolder-icons'>
                                    {this.documents?.map(doc => (
                                        <tr key={doc.uuid}>
                                            <td style={{ paddingRight: 10, paddingLeft: 22 }} className='file'>
                                                {doc.name}
                                            </td>
                                            {!this.bulkStatusEnabled && (
                                                <td>
                                                    <Dropdown
                                                        value={doc.status}
                                                        options={this.statuses}
                                                        optionLabel='label'
                                                        useOptionAsValue={true}
                                                        onChange={e => (doc.status = e.value)}
                                                        disabled={this.isSaving || this.isLoading}
                                                        style={{ minWidth: 150 }}
                                                        scrollHeight='600px'
                                                    />
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                <footer className='flex-row gap-half padded' style={{ justifyContent: 'flex-end', paddingTop: '1rem' }}>
                    <Button onClick={this.props.onClose} disabled={this.isSaving} secondary subtle>
                        {__('general.cancel')}
                    </Button>
                    <Button onClick={this.save} disabled={!this.canSave} primary>
                        {__('general.update')}
                    </Button>
                </footer>
            </>
        );
    }
}
